import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import JsonLd, { getWebPageSchema, getBreadcrumbSchema, getArticleSchema } from '@/components/feature/JsonLd';
import { allArticles } from '@/mocks/articles';

export default function ArticleListPage() {
  return (
    <>
      <JsonLd data={getWebPageSchema(
        '漫资讯 - 爱漫画网漫画资讯与深度解读',
        '漫画圈最新动态、作品解读、使用指南。涵盖热血、恋爱、奇幻等各类漫画的深度文章。',
        'https://aimanhua.com/article/latest'
      )} />
      <JsonLd data={getBreadcrumbSchema([
        { name: '首页', url: 'https://aimanhua.com' },
        { name: '漫资讯', url: 'https://aimanhua.com/article/latest' },
      ])} />
      <Layout>
        <div className="pt-16">
          <div className="bg-background-50 border-b border-background-200/60">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-800 font-medium">漫资讯</span>
              </nav>
            </div>
          </div>

          <div className="bg-background-50 border-b border-background-200/60">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">漫资讯</h1>
              <p className="text-sm text-foreground-600 mt-2">漫画圈最新动态、作品解读、使用指南</p>
            </div>
          </div>

          <div className="py-10 md:py-14">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {allArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 group cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded whitespace-nowrap">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-[10px] bg-secondary-100 text-secondary-700 px-1.5 py-0.5 rounded whitespace-nowrap">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}

export function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <div className="pt-24 pb-20 text-center">
          <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-900 mb-2">文章未找到</h2>
          <Link to="/article/latest" className="text-primary-500 hover:text-primary-600 text-sm">
            返回文章列表
          </Link>
        </div>
      </Layout>
    );
  }

  const relatedArticles = allArticles.filter((a) => a.id !== article.id).slice(0, 3);
  const articleUrl = `https://aimanhua.com/article/${article.slug}`;

  return (
    <>
      <JsonLd data={getArticleSchema({
        title: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.date,
        dateModified: article.date,
        author: article.author,
        url: articleUrl,
      })} />
      <JsonLd data={getWebPageSchema(article.title, article.excerpt, articleUrl)} />
      <JsonLd data={getBreadcrumbSchema([
        { name: '首页', url: 'https://aimanhua.com' },
        { name: '漫资讯', url: 'https://aimanhua.com/article/latest' },
        { name: article.title, url: articleUrl },
      ])} />
      <Layout>
        <article className="pt-16">
          <div className="bg-background-50 border-b border-background-200/60">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <Link to="/article/latest" className="hover:text-primary-500 transition-colors">漫资讯</Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</span>
              </nav>
            </div>
          </div>

          <div className="bg-background-50 border-b border-background-200/60">
            <div className="w-full max-w-[900px] mx-auto px-4 md:px-6 py-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded whitespace-nowrap">{article.category}</span>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
                <span className="text-xs text-foreground-400">作者：{article.author}</span>
              </div>
              <h1 className="text-xl md:text-3xl font-heading text-foreground-950 mb-4 leading-tight">{article.title}</h1>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded-md whitespace-nowrap">{tag}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full max-w-[900px] mx-auto px-4 md:px-6 mt-8">
            <div className="rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 爱漫画网`}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6 py-10">
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          <div className="bg-background-100 py-14">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-xl font-heading text-foreground-950 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 group cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-400">{ra.date}</span>
                        <span className="text-xs text-foreground-400">{ra.readTime}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </Layout>
    </>
  );
}